import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { apiService } from '../services/api';
import BirdCard from '../components/BirdCard';
import MapLocationPicker from '../components/MapLocationPicker';
import { BirdType, Location, MessageForm, CostBreakdown } from '../types';
import { 
  calculateDistance, 
  calculateCost, 
  validateMessage, 
  estimateDeliveryTime, 
  formatDeliveryTime,
  getBestBirdForMessage 
} from '../utils/costCalculator';

const SendMessage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateBalance } = useAuthStore();
  
  const [formData, setFormData] = useState<MessageForm>({
    content: '',
    destination: null,
    selectedBird: null
  });
  
  const [origin, setOrigin] = useState<Location | null>(null);
  const [distance, setDistance] = useState<number>(0);
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown | null>(null);
  const [estimatedTime, setEstimatedTime] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  const [availableBirds, setAvailableBirds] = useState<BirdType[]>([]);

  // Fetch available bird types
  useEffect(() => {
    const fetchBirds = async () => {
      try {
        const birds = await apiService.getBirdTypes();
        // Sort birds by base cost (lowest first)
        const sortedBirds = birds.sort((a, b) => a.properties.baseCost - b.properties.baseCost);
        setAvailableBirds(sortedBirds);
      } catch (error) {
        console.error('Failed to fetch bird types:', error);
        // Show error - no fallback to mock data
        setAvailableBirds([]);
        alert('Failed to load bird types. Please check your connection and try again.');
      }
    };

    fetchBirds();
  }, []);

  // Calculate distance when origin and destination change
  useEffect(() => {
    if (origin && formData.destination) {
      const dist = calculateDistance(
        origin.latitude,
        origin.longitude,
        formData.destination.latitude,
        formData.destination.longitude
      );
      setDistance(dist);
      // No auto-advance - let users manually proceed
    } else {
      setDistance(0);
      // Go back to step 1 if locations are cleared and we're on a later step
      if (currentStep > 1 && (!origin || !formData.destination)) {
        setCurrentStep(1);
      }
    }
  }, [origin, formData.destination, currentStep]);

  // Calculate cost and time when bird, distance, or message changes
  useEffect(() => {
    if (formData.selectedBird && distance > 0 && formData.content) {
      const cost = calculateCost(formData.content, distance, formData.selectedBird);
      const time = estimateDeliveryTime(distance, formData.selectedBird);
      
      setCostBreakdown(cost);
      setEstimatedTime(formatDeliveryTime(time));
    } else {
      setCostBreakdown(null);
      setEstimatedTime('');
    }
  }, [formData.selectedBird, distance, formData.content]);

  const handleBirdSelection = (bird: BirdType) => {
    setFormData(prev => ({
      ...prev,
      selectedBird: prev.selectedBird?.id === bird.id ? null : bird
    }));
    // No auto-advance - let users manually proceed to step 3
  };

  const goToStep = (step: 1 | 2 | 3) => {
    // Only allow going to steps that are accessible
    if (step === 1) {
      setCurrentStep(1);
    } else if (step === 2 && origin && formData.destination) {
      setCurrentStep(2);
    } else if (step === 3 && origin && formData.destination && formData.selectedBird) {
      setCurrentStep(3);
    }
  };

  const canAccessStep = (step: 1 | 2 | 3): boolean => {
    if (step === 1) return true;
    if (step === 2) return !!(origin && formData.destination);
    if (step === 3) return !!(origin && formData.destination && formData.selectedBird);
    return false;
  };

  const handleMessageChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const getRecommendedBird = () => {
    if (formData.content && distance > 0) {
      return getBestBirdForMessage(formData.content, distance, availableBirds);
    }
    return null;
  };

  const handleShowRecommendation = () => {
    const recommended = getRecommendedBird();
    if (recommended) {
      setFormData(prev => ({ ...prev, selectedBird: recommended }));
      setShowRecommendation(false);
    }
  };

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.content.trim()) {
      errors.push('Message content is required');
    }
    
    if (!origin) {
      errors.push('Origin location is required');
    }
    
    if (!formData.destination) {
      errors.push('Destination is required');
    }
    
    if (!formData.selectedBird) {
      errors.push('Please select a messenger bird');
    }
    
    if (formData.selectedBird && formData.content && distance > 0) {
      const validation = validateMessage(formData.content, distance, formData.selectedBird);
      if (!validation.isValid) {
        errors.push(...validation.errors);
      }
    }
    
    // No balance validation - we're using accumulating spending model
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }

    if (!user || !formData.selectedBird || !origin || !formData.destination || !costBreakdown) {
      return;
    }

    setIsSubmitting(true);

    try {

      // Send message via API
      const newMessage = await apiService.sendMessage({
        content: formData.content,
        origin,
        destination: formData.destination!,
        birdTypeId: formData.selectedBird!.id
      });

      // Add cost to user balance (accumulating spending model)
      updateBalance(costBreakdown.total);

      // Navigate to message details
      navigate(`/messages/${newMessage.id}`);
    } catch (error) {
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formErrors = validateForm();
  const canSubmit = formErrors.length === 0 && !isSubmitting;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Send a Message</h1>
        <p className="text-gray-600 dark:text-gray-300">
          Follow the steps to send your message with our feathered messengers
        </p>
      </div>

      {/* Step Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center space-x-8">
          <button
            onClick={() => goToStep(1)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentStep === 1 
                ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' 
                : canAccessStep(1)
                ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep === 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}>1</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">Locations</span>
          </button>
          
          <div className="w-12 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
          
          <button
            onClick={() => goToStep(2)}
            disabled={!canAccessStep(2)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentStep === 2 
                ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' 
                : canAccessStep(2)
                ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep === 2 ? 'bg-blue-600 text-white' : canAccessStep(2) ? 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
            }`}>2</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">Choose Bird</span>
          </button>
          
          <div className="w-12 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
          
          <button
            onClick={() => goToStep(3)}
            disabled={!canAccessStep(3)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentStep === 3 
                ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' 
                : canAccessStep(3)
                ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              currentStep === 3 ? 'bg-blue-600 text-white' : canAccessStep(3) ? 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
            }`}>3</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">Write Message</span>
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Step 1: Locations */}
        {currentStep === 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Step 1: Choose Origin and Destination</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MapLocationPicker
                label="From (Origin)"
                location={origin}
                onLocationChange={setOrigin}
                placeholder="Enter origin address or click map..."
                useCurrentLocation={true}
                autoShowMap={true}
                autoGetCurrentLocation={true}
              />
              
              <MapLocationPicker
                label="To (Destination)"
                location={formData.destination}
                onLocationChange={(location) => setFormData(prev => ({ ...prev, destination: location }))}
                placeholder="Enter destination address or use current location..."
                useCurrentLocation={true}
                autoShowMap={true}
                autoGetCurrentLocation={true}
              />
            </div>

            {distance > 0 && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-blue-900 dark:text-blue-100">Route Calculated</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Distance: {distance.toFixed(1)} km</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
                  >
                    Next: Choose Bird →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Bird Selection */}
        {currentStep === 2 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Step 2: Choose Your Messenger Bird</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  All {availableBirds.length} birds available for {distance.toFixed(1)} km delivery (sorted by cost)
                  <span className="text-amber-600 dark:text-amber-400 ml-2">
                    ⚠️ Birds may fail if pushed beyond their limits!
                  </span>
                </p>
              </div>
              
              {distance > 0 && !showRecommendation && (
                <button
                  type="button"
                  onClick={() => setShowRecommendation(true)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                >
                  Get Recommendation
                </button>
              )}
            </div>

            {showRecommendation && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-green-900 dark:text-green-100 mb-1">Recommended Bird</h3>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      For {distance.toFixed(1)} km delivery, we recommend the{' '}
                      <strong className="text-green-800 dark:text-green-200">{getBestBirdForMessage('', distance, availableBirds)?.name}</strong> for optimal balance of cost, speed, and reliability.
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={handleShowRecommendation}
                      className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                    >
                      Select
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRecommendation(false)}
                      className="text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableBirds.map((bird) => {
                const cost = distance > 0 
                  ? calculateCost('', distance, bird) // Empty message for now
                  : null;
                
                const time = distance > 0 
                  ? formatDeliveryTime(estimateDeliveryTime(distance, bird))
                  : undefined;

                // Calculate risk warnings instead of blocking
                const riskWarnings: string[] = [];
                if (distance > 0 && distance > bird.properties.maxDistance) {
                  const overDistance = distance - bird.properties.maxDistance;
                  const riskLevel = overDistance / bird.properties.maxDistance;
                  
                  if (riskLevel > 2) {
                    riskWarnings.push(`⚠️ EXTREME RISK: ${overDistance.toFixed(0)}km over limit! Bird likely to fall from sky!`);
                  } else if (riskLevel > 1) {
                    riskWarnings.push(`⚠️ HIGH RISK: ${overDistance.toFixed(0)}km over limit! Delivery may fail!`);
                  } else if (riskLevel > 0.5) {
                    riskWarnings.push(`⚠️ MODERATE RISK: ${overDistance.toFixed(0)}km over limit. Reduced reliability.`);
                  } else {
                    riskWarnings.push(`⚠️ SLIGHT RISK: ${overDistance.toFixed(0)}km over limit. Proceed with caution.`);
                  }
                }

                return (
                  <BirdCard
                    key={bird.id}
                    bird={bird}
                    isSelected={formData.selectedBird?.id === bird.id}
                    onClick={() => handleBirdSelection(bird)}
                    disabled={!bird.availability}
                    validationErrors={riskWarnings}
                    estimatedCost={cost?.total}
                    estimatedTime={time}
                  />
                );
              })}
            </div>

            {formData.selectedBird && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-green-900 dark:text-green-100">Bird Selected: {formData.selectedBird.name}</h3>
                    <p className="text-sm text-green-700 dark:text-green-300">Ready to compose your message</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 transition-colors"
                  >
                    Next: Write Message →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Message Composition & Review */}
        {currentStep === 3 && formData.selectedBird && (
          <div className="space-y-6 animate-fadeIn">
            {/* Message Composition */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Step 3: Compose Your Message</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message Content
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => handleMessageChange(e.target.value)}
                    placeholder="Write your message here..."
                    rows={6}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700"
                  />
                  <div className="mt-2 flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>Characters: {formData.content.length}</span>
                    <span className={formData.content.length > formData.selectedBird.properties.maxMessageLength ? 'text-red-500 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                      Max for {formData.selectedBird.name}: {formData.selectedBird.properties.maxMessageLength}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Summary & Submit */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Review & Send</h2>
              
              {!costBreakdown && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>Start typing your message to see cost calculation...</p>
                </div>
              )}
              
              {costBreakdown && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Cost Breakdown */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">Cost Breakdown</h3>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                          <span>Base cost ({formData.selectedBird.name}):</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">${costBreakdown.baseCost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                          <span>Distance ({distance.toFixed(1)} km):</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">${costBreakdown.distanceCost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                          <span>Message ({formData.content.length} chars):</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">${costBreakdown.messageCost.toFixed(2)}</span>
                        </div>
                        <hr className="my-2 border-gray-300 dark:border-gray-600" />
                        <div className="flex justify-between font-semibold text-gray-900 dark:text-gray-100">
                          <span>Total:</span>
                          <span>${costBreakdown.total.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {user && (
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <div className="flex justify-between">
                            <span>Current spending:</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              ${(user.total_spent || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between font-medium">
                            <span className="text-gray-900 dark:text-gray-100">After this message:</span>
                            <span className="text-blue-600 dark:text-blue-400">${((user.total_spent || 0) + costBreakdown.total).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Delivery Summary */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">Delivery Summary</h3>
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex justify-between">
                          <span>Messenger:</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formData.selectedBird.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Estimated time:</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{estimatedTime}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Reliability:</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formData.selectedBird.properties.reliability}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Distance:</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{distance.toFixed(1)} km</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="loading-spinner mr-2"></div>
                          Sending Message...
                        </div>
                      ) : (
                        `Send Message for $${costBreakdown.total.toFixed(2)}`
                      )}
                    </button>
                    
                    {formErrors.length > 0 && (
                      <div className="mt-3 text-sm text-red-600">
                        <p className="font-medium">Please fix the following:</p>
                        <ul className="list-disc list-inside mt-1">
                          {formErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default SendMessage;
